import { useEffect, useRef } from 'react'

/**
 * Scroll-triggered fade-up using IntersectionObserver. Tiny — about
 * 30 lines including this comment — and ships zero KB of library
 * code. Pair with the `.reveal` class in index.css.
 *
 * Usage:
 *   const ref = useReveal<HTMLDivElement>()
 *   <div ref={ref} className="reveal" data-reveal-delay="1">…</div>
 *
 * The class transitions from opacity 0 / translateY(16px) → opacity 1
 * once the element enters the viewport. One-shot — once revealed,
 * the observer disconnects.
 */
export function useReveal<T extends Element>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    // Honor reduced motion — show immediately, no observer needed.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      node.classList.add('is-visible')
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return ref
}
